import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import handleOpenSweetAlert from '../../../components/handleOpenSweetAlert';
import { useSession } from '../../../components/hooks/useSession';
import {
  getTablaPagosAut,
  autorizarPago,
  cancelarPago,
  obtenerArchivosPago,
} from '../../../services/pagosAutService';
import config from '../../../config/config';
import { buildFilterPayload } from '../../../utils/filterUtils';

const getMimeTypeFromExtension = (ext) => {
  switch (ext) {
    case 'pdf':
      return 'application/pdf';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
    case 'xml':
      return 'application/xml';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'ppt':
      return 'application/vnd.ms-powerpoint';
    case 'pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    default:
      return 'application/octet-stream';
  }
};

const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];

  if (Array.isArray(idEmpresa)) {
    return idEmpresa
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
  }

  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();

    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) {
          return arr
            .map((x) => Number(x))
            .filter((n) => Number.isFinite(n) && n > 0);
        }
      } catch {
        // ignore
      }
    }

    if (s.includes(',')) {
      return s
        .split(',')
        .map((x) => Number(x.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
    }

    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }

  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

const buildEmpresaScope = (idEmpresa) => {
  const arr = normalizeEmpresaArray(idEmpresa)
    .filter((n) => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b);

  return arr.length ? arr.join(',') : '0';
};

const buildSwalActionOptions = ({
  title = 'Confirmación',
  confirmText = 'Aceptar',
  cancelText = 'Cancelar',
  confirmColor = '#1976d2',
  width = '32rem',
  extra = {},
} = {}) => ({
  useHtml: true,
  title,
  showCancelButton: true,
  confirmButtonText: confirmText,
  cancelButtonText: cancelText,
  confirmButtonColor: confirmColor,
  focusCancel: true,
  width,
  ...extra,
});

const buildWarningHtml = (row, accionTexto) => {
  const diasVencidos = Math.abs(row.DIASRESTANTES ?? 0);
  const folio = row.FOLIO || row.ID || 'N/D';
  const proveedor = row.PROVEEDOR || 'N/D';
  const fechaSolicitud = row.FECHA || row.FECHASOLICITUD || 'N/D';
  const monto = row.MONTO || row.MONTOSOLICITADO || 'N/D';
  const fechaVenc = row.VENCIMIENTO || row.FECHA_VENCIMIENTO || 'N/D';
  const departamento = row.DEPARTAMENTO || 'N/D';
  const centroCostos = row.NUMEROCC || row.CENTROCOSTO || 'N/D';

  return `
    <div style="font-size:0.96rem; color:#424242; text-align:left;">
      <div style="
        margin-bottom:1rem;
        padding:0.9rem 1rem;
        background:#fff3e0;
        border:1px solid #ffe0b2;
        border-radius:10px;
        text-align:center;
      ">
        <div style="font-size:1.05rem; font-weight:700; color:#e65100; margin-bottom:0.35rem;">
          Precios con vigencia vencida
        </div>
        <div style="font-size:0.95rem; color:#5d4037;">
          Estás por <b>${accionTexto}</b> una solicitud con precios vencidos.
        </div>
        <div style="margin-top:0.6rem; font-size:1rem; font-weight:700; color:#c62828;">
          Días vencidos: ${diasVencidos}
        </div>
      </div>

      <div style="
        border:1px solid #e0e0e0;
        border-radius:10px;
        padding:0.9rem 1rem;
        background:#fafafa;
      ">
        <div style="margin-bottom:0.45rem;"><b>Folio OC:</b> ${folio}</div>
        <div style="margin-bottom:0.45rem;"><b>Monto solicitado:</b> ${monto}</div>
        <div style="margin-bottom:0.45rem;"><b>Fecha solicitud:</b> ${fechaSolicitud}</div>
        <div style="margin-bottom:0.45rem;"><b>Fecha vencimiento:</b> ${fechaVenc}</div>
        <div style="margin-bottom:0.45rem;"><b>Proveedor:</b> ${proveedor}</div>
        <div style="margin-bottom:0.45rem;"><b>Centro de costo:</b> ${centroCostos}</div>
        <div><b>Departamento:</b> ${departamento}</div>
      </div>

      <div style="
        margin-top:1rem;
        padding:0.75rem 0.9rem;
        background:#ffebee;
        border:1px solid #ffcdd2;
        border-radius:10px;
        color:#b71c1c;
        text-align:center;
      ">
        Si continúas, la operación quedará registrada con precios vencidos.
      </div>
    </div>
  `;
};

const buildCancelConfirmHtml = (state) => {
  const {
    diasRestantes,
    folio,
    proveedor,
    fechaSolicitud,
    monto,
    fechaVencimiento,
    observaciones,
    departamento,
    centroCostos,
  } = state;

  const diasAbs = Math.abs(diasRestantes ?? 0);

  return `
    <div style="font-size:0.96rem; color:#424242; text-align:left;">
      <div style="
        margin-bottom:1rem;
        padding:0.9rem 1rem;
        background:#ffebee;
        border:1px solid #ffcdd2;
        border-radius:10px;
        text-align:center;
      ">
        <div style="font-size:1.05rem; font-weight:700; color:#c62828; margin-bottom:0.35rem;">
          Rechazo con precios vencidos
        </div>
        <div style="font-size:0.95rem; color:#5f2120;">
          Estás por <b>RECHAZAR</b> una solicitud con precios vencidos.
        </div>
        <div style="margin-top:0.6rem; font-size:1rem; font-weight:700; color:#b71c1c;">
          Días vencidos: ${diasAbs}
        </div>
      </div>

      <div style="
        border:1px solid #e0e0e0;
        border-radius:10px;
        padding:0.9rem 1rem;
        background:#fafafa;
      ">
        <div style="margin-bottom:0.45rem;"><b>Folio OC:</b> ${folio}</div>
        <div style="margin-bottom:0.45rem;"><b>Monto solicitado:</b> ${monto}</div>
        <div style="margin-bottom:0.45rem;"><b>Fecha solicitud:</b> ${fechaSolicitud}</div>
        <div style="margin-bottom:0.45rem;"><b>Fecha vencimiento:</b> ${fechaVencimiento}</div>
        <div style="margin-bottom:0.45rem;"><b>Proveedor:</b> ${proveedor}</div>
        <div style="margin-bottom:0.45rem;"><b>Centro de costo:</b> ${centroCostos}</div>
        <div style="margin-bottom:0.8rem;"><b>Departamento:</b> ${departamento}</div>

        <div style="
          margin-top:0.75rem;
          padding:0.8rem;
          background:#fff8e1;
          border:1px solid #ffe082;
          border-radius:8px;
        ">
          <div style="font-weight:700; margin-bottom:0.35rem; color:#8d6e63;">
            Motivo de rechazo
          </div>
          <div style="white-space:pre-wrap; color:#424242;">
            ${observaciones || 'Sin comentarios'}
          </div>
        </div>
      </div>
    </div>
  `;
};

export const usePagosAutController = (options = {}) => {
  const apiUrl = config.apiBaseUrl;
  const { loggedInUsername, nombreEmpresa, idEmpresa, nombrePerfil } = useSession();

  const usuarioId = useMemo(
    () => Number(sessionStorage.getItem('UsuarioId') || 0),
    []
  );

  const esTesorero = useMemo(() => {
    return typeof nombrePerfil === 'string' &&
      nombrePerfil.toLowerCase().includes('tesorero');
  }, [nombrePerfil]);

  const empresaScope = useMemo(() => buildEmpresaScope(idEmpresa), [idEmpresa]);

  const empresaIds = useMemo(() => {
    if (!empresaScope || empresaScope === '0') return [];
    return empresaScope
      .split(',')
      .map((n) => Number(n))
      .filter((x) => Number.isFinite(x) && x > 0);
  }, [empresaScope]);

  const empresaIdPersist = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.length === 1 ? arr[0] : 0;
  }, [idEmpresa]);

  const [data, setData] = useState({ contenido: [] });
  const [loadingData, setLoadingData] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  const [fileList, setFileList] = useState([]);
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [initialFileId, setInitialFileId] = useState(null);


  const [modalStates, setModalStates] = useState({
    openCancelacionModal: false,
    observaciones: '',
    cancelarID: null,
    cancelarEmpresa: null,
    diasRestantes: null,
    folio: '',
    proveedor: '',
    fechaSolicitud: '',
    monto: '',
  });

  const monthDates = useMemo(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d) => d.toLocaleDateString('en-CA');
    return { start: fmt(firstDay), end: fmt(lastDay) };
  }, []);

  const baseFilters = useMemo(() => {
    const initialEmpId = empresaIds.length === 1 ? empresaIds[0] : null;
    const initialEmpName = initialEmpId ? nombreEmpresa : '';

    return {
      FECHA_INICIO: monthDates.start,
      FECHA_FIN: monthDates.end,
      FOLIO: '',
      EMPRESA: initialEmpName,
      EMPRESA_IDS: initialEmpId ? [initialEmpId] : [],
      PROYECTO: '',
      PROYECTOS: [],
      DIVISION: '',
      DIVISIONES: [],
      PROVEEDOR: '',
      PROVEEDORES: [],
      NUMERO_CENTRO_COSTO: '',
      CENTRO_COSTOS: [],
      DEPARTAMENTO: '',
      DEPARTAMENTOS: [],
      CONDICION_PAGO: '',
      DIVISA: '',
      DIVISAS: [],
      SOLICITANTE: '',
      ECONOMICO: '',
      ESTATUS: '',
    };
  }, [monthDates, empresaIds, nombreEmpresa]);

  const [tempFilterData, setTempFilterData] = useState(baseFilters);
  const [filterData, setFilterData] = useState(baseFilters);

  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  useEffect(() => { pageRef.current = page; }, [page]);

  const [tableState, setTableState] = useState({ globalQuery: '', sortField: null, sortDir: null });
  const tableStateRef = useRef(tableState);
  useEffect(() => { tableStateRef.current = tableState; }, [tableState]);

  const hiddenFieldsMemo = useMemo(() => {
    const authFields = [];
    for (let i = 1; i <= 20; i++) {
      authFields.push(
        `AUT${i}`,
        `AUT${i}_FECHA`,
        `AUTNIV${i}`,
        `AUTNIV${i}_NOMBRE`,
        `AUTNIV${i}_FECHA`,
        `FECHA_AUT${i}`,
        `FECHA_AUTORIZACION${i}`,
      );
    }

    const base = [
      ...authFields,
      'ID',
      'EMPRESA',
      'FOLIO',
      'FECHA',
      'TOTALOC',
      'DIVISA',
      'MONTOSOLICITADO',
      'NUMEROCC',
      'CENTROCOSTO',
      'DEPARTAMENTO',
      'COMPRADOR',
      'NOTAS',
      'NUMERO_ECO',
      'FOLIO_FACTURA',
      'CONCEPTOPAGO',
      'CONCEPTO_PAGO_ERP',
      'OBSERVACIONES',
      'REQUISITOR',
      'VENCIMIENTO',
      'SOLICITANTE',
      'ESTATUS',
      'HAS_FACTURA',
      'ID_FACTURA',
      'HAS_CB',
      'ID_CB',
      'HAS_OC',
      'ID_OC',
      'DIAS_VENCIMIENTO_RAW',
      'cadenaAut',
      'HAS_OTROS',
      'ID_OTROS',
      'MOTIVO_CORRECCION',
      'DETALLE_CORRECCION',
      'SECCIONES_CORREGIR',
      'REQUIERE_RECLASIFICACION',
      'SUCURSAL',
      'DIASRESTANTES',
    ];

    if (!esTesorero) return base;

    return [
      ...base,
    ];
  }, [esTesorero]);

  // Resolución automática de CCs si se filtra por Proyecto o Grupo (División)
  const resolvedCCsForFilter = useMemo(() => {
    const directCCs = Array.isArray(filterData.CENTRO_COSTOS) ? filterData.CENTRO_COSTOS : [];
    if (directCCs.length > 0) {
      return directCCs.map(cc => {
        const parts = String(cc).split(' - ');
        return parts[0].trim();
      });
    }

    const proyIds = (filterData.PROYECTOS || []).map(Number);
    const divIds = (filterData.DIVISIONES || []).map(Number);

    if ((proyIds.length > 0 || divIds.length > 0) && (options.centrosCostoCatalogo || []).length > 0) {
      const matchingCCs = options.centrosCostoCatalogo.filter(cc => {
        const pId = Number(cc.ID_PROYECTO);
        const dId = Number(cc.ID_DIVISION);
        if (proyIds.length > 0 && !proyIds.includes(pId)) return false;
        if (divIds.length > 0 && !divIds.includes(dId)) return false;
        return true;
      });

      return matchingCCs.map(cc => String(cc.NUMEROCC || cc.NUMERO_CC || cc.CENTROCOSTO || '').trim()).filter(Boolean);
    }

    return null;
  }, [filterData.CENTRO_COSTOS, filterData.PROYECTOS, filterData.DIVISIONES, options.centrosCostoCatalogo]);

  const abortRef = useRef(null);

  const fetchData = useCallback(async (isLoadMore = false) => {
    if (!apiUrl || !loggedInUsername || !empresaIds.length) return;

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const currentPage = isLoadMore === true ? (pageRef.current + 1) : 1;
    setLoadingData(true);
    setError(null);

    try {
      const activeEmpresas = (filterData.EMPRESA_IDS || []).length ? filterData.EMPRESA_IDS : empresaIds;

      const serverFilterData = {
        ...filterData,
        PROVEEDOR: (filterData.PROVEEDORES || []).length ? filterData.PROVEEDORES.join(',') : filterData.PROVEEDOR,
        DEPARTAMENTO: (filterData.DEPARTAMENTOS || []).length ? filterData.DEPARTAMENTOS.join(',') : filterData.DEPARTAMENTO,
        DIVISA: (filterData.DIVISAS || []).length ? filterData.DIVISAS.join(',') : filterData.DIVISA,
        NUMERO_CENTRO_COSTO: resolvedCCsForFilter !== null ? resolvedCCsForFilter.join(',') : (filterData.NUMERO_CENTRO_COSTO || ''),
      };

      const combined = {
        ...buildFilterPayload(serverFilterData),
        globalQuery: tableStateRef.current.globalQuery || '',
        sortField: tableStateRef.current.sortField || null,
        sortDir: tableStateRef.current.sortDir || null,
        SESSION: loggedInUsername,
        page: currentPage,
        pageSize: 100
      };
      const respData = await getTablaPagosAut(
        {
          filterData: combined,
          nombreEmpresa,
          idEmpresa: activeEmpresas,
          nombrePerfil,
        },
        controller.signal
      );

      setData(prev => {
        if (isLoadMore === true && respData?.contenido) {
          return {
            ...respData,
            contenido: [...(prev?.contenido || []), ...respData.contenido]
          };
        }
        return respData || { contenido: [], total: 0 };
      });
      setPage(currentPage);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      setError('Error cargando solicitudes (' + (err?.response?.data?.message || err.message) + ')');
    } finally {
      if (abortRef.current === controller) {
        setLoadingData(false);
        abortRef.current = null;
      }
    }
  }, [apiUrl, filterData, nombreEmpresa, empresaIds, loggedInUsername, nombrePerfil, resolvedCCsForFilter]);

  const handleExportFetchAll = useCallback(async () => {
    if (!apiUrl || !loggedInUsername || !empresaIds.length) return data;

    try {
      const activeEmpresas = (filterData.EMPRESA_IDS || []).length ? filterData.EMPRESA_IDS : empresaIds;
      const serverFilterData = {
        ...filterData,
        PROVEEDOR: (filterData.PROVEEDORES || []).length ? filterData.PROVEEDORES.join(',') : filterData.PROVEEDOR,
        DEPARTAMENTO: (filterData.DEPARTAMENTOS || []).length ? filterData.DEPARTAMENTOS.join(',') : filterData.DEPARTAMENTO,
        DIVISA: (filterData.DIVISAS || []).length ? filterData.DIVISAS.join(',') : filterData.DIVISA,
        NUMERO_CENTRO_COSTO: resolvedCCsForFilter !== null ? resolvedCCsForFilter.join(',') : (filterData.NUMERO_CENTRO_COSTO || ''),
      };

      const combined = {
        ...buildFilterPayload(serverFilterData),
        globalQuery: tableStateRef.current.globalQuery || '',
        sortField: tableStateRef.current.sortField || null,
        sortDir: tableStateRef.current.sortDir || null,
        SESSION: loggedInUsername,
        page: 1,
        pageSize: Math.max(data?.total || 5000, 5000),
      };
      
      const respData = await getTablaPagosAut({
        filterData: combined,
        nombreEmpresa,
        idEmpresa: activeEmpresas,
        nombrePerfil,
      });

      return respData || { contenido: [], total: 0 };
    } catch (err) {
      console.error('Error fetching all data for export:', err);
      return data;
    }
  }, [apiUrl, filterData, nombreEmpresa, empresaIds, loggedInUsername, nombrePerfil, data, resolvedCCsForFilter]);

  useEffect(() => {
    fetchData(false);

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [filterData, tableState, fetchData]);

  const validateAuthorizationForAction = useCallback((row, username) => {
    let nivel = 1;

    while (row[`AUT${nivel}`] !== undefined && row[`AUTNIV${nivel}`] !== undefined) {
      const aut = row[`AUT${nivel}`];
      const autnivRaw = row[`AUTNIV${nivel}`];
      const autniv = String(autnivRaw ?? '').trim();

      if (
        aut === 'N/A' ||
        autniv === 'N/A' ||
        aut === undefined ||
        autnivRaw === undefined
      ) {
        nivel++;
        continue;
      }

      const safeAut = String(aut || '').trim().toLowerCase();
      const safeUser = String(username || '').trim().toLowerCase();

      if (Number(autniv) === 0) {
        if (safeAut !== safeUser) {
          return {
            allowed: false,
            level: nivel,
            message: `El usuario (${username}) no corresponde al autorizador de nivel ${nivel} (${aut}).`,
          };
        }

        return { allowed: true, level: nivel };
      }

      nivel++;
    }

    return {
      allowed: false,
      message: 'No hay configuraciones de autorización para el usuario.',
    };
  }, []);

  const autorizarSolicitud = useCallback(async (row) => {
    const validation = validateAuthorizationForAction(row, loggedInUsername);

    if (!validation.allowed) {
      handleOpenSweetAlert(validation.message, 'error');
      return;
    }

    const nivel = validation.level;
    const esUltimoNivel =
      !row[`AUT${nivel + 1}`] || row[`AUT${nivel + 1}`] === 'N/A';

    setLoadingAction(true);

    try {
      await autorizarPago(row.ID, nivel, loggedInUsername, row.EMPRESA);
      setLoadingAction(false);
      setSnackbarMessage('Solicitud autorizada correctamente.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchData(false);
    } catch (error) {
      setLoadingAction(false);
      await handleOpenSweetAlert(
        esUltimoNivel
          ? `Error al autorizar en el último nivel ${nivel}`
          : `Error al autorizar en el nivel ${nivel}`,
        'error'
      );
    } finally {
      setLoadingAction(false);
    }
  }, [loggedInUsername, validateAuthorizationForAction, fetchData, handleOpenSweetAlert]);

  const confirmAutorizarSolicitud = useCallback(async (row) => {
    const vencida = Number(row.DIASRESTANTES ?? 0) < 0;

    if (vencida) {
      const html = buildWarningHtml(row, 'AUTORIZAR');

      const result = await handleOpenSweetAlert(
        html,
        'warning',
        buildSwalActionOptions({
          title: 'Confirmar autorización',
          confirmText: 'Sí, autorizar',
          cancelText: 'Cancelar',
          confirmColor: '#2e7d32',
          width: 'clamp(280px, 90vw, 520px)',
        })
      );

      if (!result?.isConfirmed) return;
      autorizarSolicitud(row);
      return;
    }

    const result = await handleOpenSweetAlert(
      `
        <div style="font-size:clamp(0.85rem, 0.80rem + 0.1vw, 0.95rem); color:#475569; text-align:center;">
          ¿Está seguro de que desea <b style="color:#2e7d32;">AUTORIZAR</b> la solicitud?
        </div>
      `,
      'info',
      buildSwalActionOptions({
        title: 'Confirmar autorización',
        confirmText: 'Sí, autorizar',
        cancelText: 'Cancelar',
        confirmColor: '#2e7d32',
        width: 'clamp(280px, 85vw, 420px)',
      })
    );

    if (!result?.isConfirmed) return;
    autorizarSolicitud(row);
  }, [autorizarSolicitud]);

  const handleCancelarAutorizacion = useCallback((row) => {
    const validation = validateAuthorizationForAction(row, loggedInUsername);

    if (!validation.allowed) {
      handleOpenSweetAlert(validation.message, 'error');
      return;
    }

    setModalStates((prev) => ({
      ...prev,
      observaciones: '',
      cancelarID: row.ID,
      cancelarEmpresa: row.EMPRESA,
      diasRestantes: row.DIASRESTANTES ?? 0,
      folio: row.FOLIO || row.ID || 'N/D',
      proveedor: row.PROVEEDOR || 'N/D',
      fechaSolicitud: row.FECHA || row.FECHASOLICITUD || 'N/D',
      monto: row.MONTO || row.MONTOSOLICITADO || 'N/D',
      departamento: row.DEPARTAMENTO || 'N/D',
      centroCostos: row.NUMEROCC || row.CENTROCOSTO || 'N/D',
      fechaVencimiento: row.VENCIMIENTO || row.FECHA_VENCIMIENTO || 'N/D',
      openCancelacionModal: true,
    }));
  }, [loggedInUsername, validateAuthorizationForAction]);

  const cancelarSolicitud = useCallback(async (correctionData) => {
    setModalStates((prev) => ({
      ...prev,
      openCancelacionModal: false,
    }));

    setLoadingAction(true);

    try {
      await cancelarPago({
        ID: modalStates.cancelarID,
        EMPRESA: modalStates.cancelarEmpresa,
        OBSERVACIONES: correctionData.detalle,
        USUARIO: loggedInUsername,
        MOTIVO: correctionData.motivo,
        DETALLE: correctionData.detalle,
        SECCIONES: correctionData.secciones,
        RECLASIFICAR: correctionData.reclasificar
      });

      setLoadingAction(false);
      setSnackbarMessage('Solicitud rechazada correctamente.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchData(false);
    } catch (error) {
      setLoadingAction(false);
      await handleOpenSweetAlert('Error al cancelar la solicitud.', 'error');
    } finally {
      setLoadingAction(false);
    }
  }, [modalStates, loggedInUsername, fetchData, handleOpenSweetAlert]);

  const fetchAndShowFiles = useCallback(async (row, targetFileId = null) => {
    setLoadingAction(true);
    try {
      const solId = row.ID || row.ID_SOLICITUD || row.AUTSOLPAGOID;
      const response = await axios.post(`${apiUrl}/pagosAutDescargar`, { id: solId });
      const { adjuntos = [] } = response.data || {};

      let comprobantesAdjuntos = [];
      try {
        if (solId) {
          const compResponse = await axios.post(`${apiUrl}/comprobanteAutDescargar`, { id: solId });
          comprobantesAdjuntos = compResponse.data?.adjuntos || [];
        }
      } catch (err) {
        console.warn('No se pudieron cargar comprobantes adicionales', err);
      }

      if (!adjuntos.length && !comprobantesAdjuntos.length) {
        handleOpenSweetAlert('No hay archivos adjuntos.', 'info');
        return;
      }

      let filteredAdjuntos = adjuntos;
      if (targetFileId) {
        let targetIds = [targetFileId];
        if (typeof targetFileId === 'string' && targetFileId.startsWith('[')) {
          try {
            targetIds = JSON.parse(targetFileId);
          } catch (e) {
            targetIds = [targetFileId];
          }
        }
        filteredAdjuntos = adjuntos.filter((a) => {
          const isTarget = targetIds.some((id) => String(id) === String(a.archivo_id));
          return isTarget;
        });
        setInitialFileId(targetIds);
      } else {
        setInitialFileId(null);
      }

      const deriveCategoryLocal = (filepath = '') => {
        const normalized = filepath.replace(/\\/g, '/').toLowerCase();
        if (normalized.includes('/factura/') || normalized.includes('\\factura\\')) return 'FACTURA';
        if (normalized.includes('/datos bancarios/') || normalized.includes('datos_bancarios') || normalized.includes('caratula')) return 'DATOS BANCARIOS';
        if (normalized.includes('/orden') || normalized.includes('ordencompra') || normalized.includes('/oc/')) return 'OC';
        return 'OTROS';
      };

      const files = filteredAdjuntos.map((file) => {
        const ext = file.filename.split('.').pop().toLowerCase();
        return {
          id: file.archivo_id,
          filename: file.filename,
          previewUrl: `${apiUrl}/visualizarPagosAut/${file.archivo_id}`,
          downloadUrl: `${apiUrl}/descargarPagosAut/${file.archivo_id}`,
          fileType: getMimeTypeFromExtension(ext),
          category: file.category || deriveCategoryLocal(file.filepath || ''),
        };
      });

      const officeExts = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
      const compFiles = comprobantesAdjuntos.map((file) => {
        const filename = file.FILENAME;
        const fileId = file.ID_REF;
        const ext = filename.split('.').pop().toLowerCase();
        const isOffice = officeExts.includes(ext);
        const origen = String(file.ORIGEN || '').toUpperCase();
        const rawId = String(fileId || '');

        let targetId = rawId;
        if (!rawId.startsWith('COMP_') && !rawId.startsWith('TESO_')) {
          if (origen === 'PROGRAMADAS') {
            targetId = `TESO_ADJ_${rawId}`;
          } else if (origen === 'NUEVO') {
            targetId = `COMP_NEW_${rawId}`;
          } else if (origen === 'VIEJO') {
            targetId = `COMP_OLD_${rawId}`;
          } else {
            targetId = `COMP_PAGO_${rawId}`;
          }
        }

        return {
          id: `COMP_${fileId}_${filename}`,
          filename,
          previewUrl: `${apiUrl}/visualizarPagosAut/${targetId}`,
          downloadUrl: `${apiUrl}/descargarPagosAut/${targetId}`,
          fileType: isOffice ? 'application/pdf' : getMimeTypeFromExtension(ext),
          category: 'Comprobante de pago',
        };
      });

      setFileList([...files, ...compFiles]);
      setFileModalOpen(true);
    } catch (error) {
      console.error('Error al obtener archivos adjuntos:', error);
      handleOpenSweetAlert('Error al obtener archivos adjuntos', 'error');
    } finally {
      setLoadingAction(false);
    }
  }, [apiUrl]);

  const handleViewFile = useCallback((row) => fetchAndShowFiles(row), [fetchAndShowFiles]);

  const handleViewSpecificFile = useCallback((row, fileId) => {
    if (!fileId) return;
    fetchAndShowFiles(row, fileId);
  }, [fetchAndShowFiles]);

  const procesarMasivo = useCallback(async (filas, accion) => {
    if (!filas?.length || !apiUrl || !loggedInUsername) return;

    const validRows = [];
    const invalidRows = [];

    for (const row of filas) {
      const v = validateAuthorizationForAction(row, loggedInUsername);

      if (!v.allowed) {
        invalidRows.push(`Folio ${row.FOLIO || row.ID || 'N/D'}: ${v.message}`);
      } else {
        validRows.push({
          idFolio: row.AUTSOLPAGOID || row.ID,
          nivel: v.level,
          folioOC: row.FOLIO_OC || row.FOLIO,
          empresaTabla: row.EMPRESA,
        });
      }
    }

    if (validRows.length === 0) {
      const msjErrores = invalidRows.join('<br/><br/>');

      handleOpenSweetAlert(
        `No tienes autorización para ${accion} las ${invalidRows.length} filas.<br/><br/><div style="text-align:left; font-size:13px; color:#c62828;">${msjErrores}</div>`,
        'error',
        {
          title: 'Ninguna fila válida',
          useHtml: true,
        }
      );
      return;
    }

    // Separar filas vencidas vs vigentes
    const vencidasRows = filas.filter(r => (r.DIASRESTANTES ?? 0) < 0);
    const vigentesRows = filas.filter(r => (r.DIASRESTANTES ?? 0) >= 0);

    // Folios de filas válidas, diferenciando si están vencidas o no
    const validFoliosSet = new Set(validRows.map(v => String(v.folioOC)));
    const vencidasValidas = vencidasRows.filter(r => validFoliosSet.has(String(r.FOLIO_OC || r.FOLIO)));
    const vigentesValidas  = vigentesRows.filter(r => validFoliosSet.has(String(r.FOLIO_OC || r.FOLIO)));

    const accionLabel = accion === 'autorizar' ? 'AUTORIZAR' : 'RECHAZAR';
    const accionColor = accion === 'autorizar' ? '#2e7d32' : '#c62828';

    const formatFolios = (arr, max = 5) => {
      const folios = arr.map(r => r.FOLIO_OC || r.FOLIO || r.ID || 'N/D');
      const shown = folios.slice(0, max).join(', ');
      return folios.length > max ? `${shown} y ${folios.length - max} más...` : shown;
    };

    let htmlConfirm = `
      <div style="font-size:1rem; color:#424242;">
        <div style="text-align:center; margin-bottom:1rem;">
          ¿Estás seguro de <b style="color:${accionColor}">${accionLabel}</b> 
          <b>${validRows.length}</b> ${validRows.length === 1 ? 'solicitud' : 'solicitudes'}?
        </div>
    `;

    if (vigentesValidas.length > 0) {
      htmlConfirm += `
        <div style="
          padding:0.75rem 1rem;
          background:#e8f5e9;
          border:1px solid #a5d6a7;
          border-radius:8px;
          margin-bottom:0.75rem;
        ">
          <div style="font-weight:700; color:#2e7d32; margin-bottom:0.35rem;">
            ✅ ${vigentesValidas.length} solicitud${vigentesValidas.length > 1 ? 'es' : ''} vigente${vigentesValidas.length > 1 ? 's' : ''}
          </div>
          <div style="font-size:0.88rem; color:#424242;">
            <b>Folios:</b> ${formatFolios(vigentesValidas)}
          </div>
        </div>
      `;
    }

    if (vencidasValidas.length > 0) {
      htmlConfirm += `
        <div style="
          padding:0.75rem 1rem;
          background:#fff3e0;
          border:1px solid #ffcc80;
          border-radius:8px;
          margin-bottom:0.75rem;
        ">
          <div style="font-weight:700; color:#e65100; margin-bottom:0.35rem;">
            ⚠️ ${vencidasValidas.length} solicitud${vencidasValidas.length > 1 ? 'es' : ''} con precios vencidos
          </div>
          <div style="font-size:0.88rem; color:#424242;">
            <b>Folios:</b> ${formatFolios(vencidasValidas)}
          </div>
          <div style="font-size:0.82rem; color:#bf360c; margin-top:0.4rem;">
            Estas solicitudes se ${accion === 'autorizar' ? 'autorizarán' : 'rechazarán'} igualmente, pero quedarán registradas con precios vencidos.
          </div>
        </div>
      `;
    }

    if (invalidRows.length > 0) {
      const htmlList = invalidRows
        .slice(0, 5)
        .map((e) => `<li>${e}</li>`)
        .join('');

      htmlConfirm += `
        <div style="
          padding:0.75rem;
          background:#ffebee;
          border:1px solid #ef9a9a;
          border-radius:8px;
          font-size:0.88rem;
          color:#c62828;
        ">
          ⛔ Se saltarán <b>${invalidRows.length}</b> registros sin autorización previa.
          <ul style="margin:0.4rem 0 0; padding-left:18px; font-size:0.82rem;">
            ${htmlList}
            ${invalidRows.length > 5 ? '<li>...</li>' : ''}
          </ul>
        </div>
      `;
    }

    htmlConfirm += `</div>`;


    const options = buildSwalActionOptions({
      title: accion === 'autorizar' ? 'Autorización Masiva de Solicitudes' : 'Rechazo Masivo de Solicitudes',
      confirmText: accion === 'autorizar' ? 'Confirmar Autorización' : 'Confirmar Rechazo',
      cancelText: 'Volver',
      confirmColor: accion === 'autorizar' ? '#2e7d32' : '#d32f2f',
      width: '38rem',
      extra: accion === 'cancelar' ? {
        html: `
          ${htmlConfirm}
          <div style="text-align:left; margin-top:1.5rem; border-top:1px solid #eee; padding-top:1rem;">
            <label style="font-weight:700; display:block; margin-bottom:0.5rem; color:#444;">Escribe el motivo del rechazo general:</label>
            <textarea id="swal-detalle" class="swal2-textarea" placeholder="¿Por qué se rechazan estas solicitudes?" style="width:100%; margin:0; height:100px;"></textarea>
          </div>
        `,
        preConfirm: () => {
          const detalle = document.getElementById('swal-detalle').value;
          if (!detalle || detalle.length < 5) {
            Swal.showValidationMessage('¡Escribe un motivo más descriptivo!');
            return false;
          }
          return { detalle };
        }
      } : {}
    });

    // Cambiamos handleOpenSweetAlert para usar el html que ya incluimos arriba
    const result = await handleOpenSweetAlert(accion === 'cancelar' ? '' : htmlConfirm, 'warning', options);

    if (!result?.isConfirmed) return;

    // Extraemos los datos del preConfirm si es cancelación
    const { detalle } = accion === 'cancelar' ? result.value : { detalle: '' };
    
    setLoadingAction(true);

    try {
      const payload = {
        accion,
        filas: validRows,
        nombreUsuario: loggedInUsername,
        observaciones: detalle,
        motivo: 'RECHAZO_MASIVO',
        detalle: detalle,
        secciones: 'TODAS',
        reclasificar: 0
      };

      const response = await fetch(`${apiUrl}/pagosAutProcesarMasivo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const dataResp = await response.json();

      if (!response.ok) {
        throw new Error(dataResp.error || 'Error en el servidor');
      }

      const ex = dataResp.resultados?.exitosos?.length || 0;
      const fall = dataResp.resultados?.fallidos?.length || 0;

      if (fall === 0 && invalidRows.length === 0) {
        setSnackbarMessage(`${ex} solicitudes procesadas con éxito.`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        await handleOpenSweetAlert(
          `Se procesaron <b>${ex}</b> registros.<br>Fallaron / Ignorados: <b>${fall + invalidRows.length}</b>.`,
          fall > 0 ? 'error' : 'warning',
          {
            useHtml: true,
            title: 'Procesamiento parcial',
          }
        );
      }
      return true;
    } catch (error) {
      console.error('Error masivo:', error);
      await handleOpenSweetAlert(error.message || 'Error al procesar las solicitudes.', 'error');
      return false;
    } finally {
      setLoadingAction(false);
      fetchData(false); // Refrescar tabla en segundo plano
    }
  }, [apiUrl, loggedInUsername, validateAuthorizationForAction, fetchData, handleOpenSweetAlert]);

  const ejecutarBusquedaManual = useCallback(() => {
    setFilterData(tempFilterData);
    setPage(1);
  }, [tempFilterData]);

  const aplicarFiltros = useCallback((nextFilters = tempFilterData, quickSearch = tableStateRef.current.globalQuery || '') => {
    const cleanSearch = String(quickSearch || '').trim();
    tableStateRef.current = {
      ...tableStateRef.current,
      globalQuery: cleanSearch,
    };
    setFilterData(nextFilters);
    setTableState(prev => ({
      ...prev,
      globalQuery: cleanSearch,
    }));
    setPage(1);
  }, [tempFilterData]);

  const handleTableStateChange = useCallback((newState) => {
    const newGQ = newState.globalQuery || '';
    const newSF = newState.sortConfig?.field || null;
    const newSD = newState.sortConfig?.direction || null;
    const cur = tableStateRef.current;

    if (cur.globalQuery !== newGQ || cur.sortField !== newSF || cur.sortDir !== newSD) {
      setTableState({ globalQuery: newGQ, sortField: newSF, sortDir: newSD });
      setPage(1);
    }
  }, []);

  return {
    data,
    loadingData,
    loadingAction,
    error,
    tempFilterData,
    setTempFilterData,
    ejecutarBusquedaManual,
    aplicarFiltros,
    snackbarOpen,
    snackbarOpen,
    setSnackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    fileModalOpen,
    setFileModalOpen,
    fileList,
    initialFileId,
    modalStates,
    setModalStates,
    empresaScope,
    empresaIdPersist,
    empresaIds,
    baseFilters,
    nombreEmpresa,
    usuarioId,
    esTesorero,
    hiddenFieldsMemo,
    confirmAutorizarSolicitud,
    handleCancelarAutorizacion,
    cancelarSolicitud,
    handleViewFile,
    handleViewSpecificFile,
    procesarMasivo,
    handleExportFetchAll,
    fetchData,
    handleTableStateChange,
  };
};
